import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.licensingJobs,
  jobViewItemsPerPage: PAGINATION_STORAGE_KEYS.licensingJobsJobView,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialJobViewItemsPerPage = getStoredItemsPerPage(
  paginationKeys.jobViewItemsPerPage,
);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'DESC']
 */
const getInitialJobsSort = () => {
  return safeGetJSON("licJobSort", ["id", "DESC"]);
};

/**
 * Initial state for the job view modal (task list within a job).
 * Separated for use with CLEAR_JOB_VIEW_MODAL action.
 */
const initialJobViewState = {
  jobViewTasks: [],
  jobViewFilterForm: {
    jobId: "",
    jobStatus: "",
    dealsProcessed: "",
    taskStatus: {
      value: "",
      label: "All",
    },
  },
  isJobViewModalOpen: false,
  taskStatusOptions: [
    { value: "", label: "All" },
    { value: "COMPLETE", label: "Successes" },
    { value: "INVALID", label: "Failures" },
  ],
  /** Job statuses that indicate the job is still processing and may need polling */
  pendingStatuses: [
    "RUNNING",
    "CREATED",
    "STARTING",
    "RETRYING",
    "QUEUED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED",
    "QUEUED_UPDATED_USER_INITIATED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED_ADMINISTRATOR",
  ],
  jobViewTotalPages: 0,
  jobViewCurrentPage: 0,
  jobViewTotalItems: 0,
  jobViewItemsPerPage: initialJobViewItemsPerPage,
};

/**
 * LicensingJobs Redux State Shape
 * @typedef {Object} LicensingJobsState
 * @property {Array} jobs - List of licensing job records from API
 * @property {Array} jobStatuses - Available job status values
 * @property {Object} filterForm - Search/filter form values
 * @property {Object} licensingJobForm - Create/edit form values
 * @property {Object|null} selectedJob - Currently selected job for viewing
 * @property {Array} selectedJobHistory - Audit history for selected job
 * @property {string} selectedHistory - Selected history entry ID
 * @property {boolean} isJobViewDrawerOpen - Job view drawer visibility
 * @property {boolean} isEditDrawerOpen - Edit drawer visibility
 * @property {boolean} isJobStatusDrawerOpen - Status drawer visibility
 * @property {boolean} isUploadTasksDrawerOpen - Upload validation tasks drawer visibility
 * @property {Array} licensingJobUploadTasks - Validation tasks from the last upload
 * @property {boolean} isOfferModalOpen - Offer modal visibility
 * @property {Array} offers - Offers associated with selected job
 * @property {Object|null} selectedOffer - Currently selected offer
 * @property {boolean} isOfferHistoryModalOpen - Offer history modal visibility
 * @property {Array} offerMediaOptions - Media options for offer filtering (from API)
 * @property {Array} offerTerritoryOptions - Territory options for offer filtering (from API)
 * @property {Array} licenseeTypeOptions - Licensee type options (GLOBAL, LOCAL)
 * @property {Array} statusOptions - Job status dropdown options
 * @property {Array} licJobRange - Pagination range [start, end]
 * @property {Array} licJobSort - Sort tuple [field, direction]
 * @property {boolean} refreshJobs - Flag to trigger grid data refetch
 */
const initialState = {
  jobs: [],
  jobStatuses: [],
  filterForm: {
    jobId: "",
    catalogId: "",
    catalogName: "",
    users: "",
    status: "",
    media: "",
    territory: "",
    licenseeType: "",
    tprDisplay: "hide",
  },
  licensingJobForm: {
    description: "",
    startDate: "01/01/1900",
    endDate: "12/31/3000",
    catalogs: [],
    deals: [],
    medias: [],
    territories: [],
    licenseeTypes: [],
  },
  selectedJob: null,
  selectedJobHistory: [],
  selectedHistory: "",
  isJobViewDrawerOpen: false,
  isEditDrawerOpen: false,
  isJobStatusDrawerOpen: false,
  isUploadTasksDrawerOpen: false,
  licensingJobUploadTasks: [],
  isOfferModalOpen: false,
  offers: [],
  selectedOffer: null,
  isOfferHistoryModalOpen: false,
  offersTotalPages: 0,
  offersTotalItems: 0,
  offersCurrentPage: 0,
  offerMediaOptions: [],
  offerTerritoryOptions: [],
  offerLanguageOptions: [],
  offerSubDubLanguageOptions: [],
  offerFormatOptions: [],
  offerCurrencyOptions: [],
  offerTransactionalWindowOptions: [],
  isAdmin: false,
  jobStatusOptions: [],
  mediaOptions: [],
  userOptions: [],
  territoryOptions: [],
  licenseeTypeOptions: [
    { value: "GLOBAL", label: "Global" },
    { value: "LOCAL", label: "Local" },
  ],
  statusOptions: [
    { value: "CREATED", label: "Created" },
    { value: "PENDING", label: "Pending" },
    { value: "RUNNING", label: "Running" },
    { value: "COMPLETE", label: "Complete" },
    { value: "FAILED", label: "Failed" },
  ],
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  filterParam: "",
  licJobRange: initialPaginationRange,
  licJobSort: getInitialJobsSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  refreshJobs: false,
  ...initialJobViewState,
};

export function licensingJobs(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_JOBS: {
      // Persist sort preference to localStorage for session continuity
      if (payload.licJobSort) {
        localStorage.setItem("licJobSort", JSON.stringify(payload.licJobSort));
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_JOBS_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.UPDATE_JOBS_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_JOBS: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          licJobSort: state.licJobSort,
          mediaOptions: state.mediaOptions,
          territoryOptions: state.territoryOptions,
          jobStatusOptions: state.jobStatusOptions,
          userOptions: state.userOptions,
        },
        ["licJobRange"],
        ["itemsPerPage", "jobViewItemsPerPage"],
      );
    }
    case CASES.CLEAR_JOB_VIEW_MODAL: {
      return {
        ...state,
        ...initialJobViewState,
        jobViewItemsPerPage: state.jobViewItemsPerPage,
        selectedJob: "",
      };
    }
    case CASES.CLEAR_JOB_TASKS_MODAL: {
      return {
        ...state,
        selectedJob: "",
      };
    }
    default:
      return state;
  }
}
